import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Info, TrendingUp, ChevronRight, AlertCircle, CheckCircle } from 'lucide-react';

const ProfileDiagnosticsModal = ({ 
  isOpen, 
  onClose, 
  completeness, 
  inconsistencies = [], 
  aiConfidence = 0,
  onNavigateToField,
  impactForecasts = [],
  initialTab = 'missing'
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  // Update activeTab when initialTab changes
  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  // Early return if modal is not open or data is not ready
  if (!isOpen || !completeness) return null;

  // Map field names to section IDs
  const fieldToSection = {
    // Basic Info
    name: 'basicInfo',
    grade_level: 'basicInfo',
    graduation_year: 'basicInfo',
    state_of_residency: 'basicInfo',
    
    // School Info
    high_school_name: 'schoolInfo',
    high_school_city: 'schoolInfo',
    high_school_state: 'schoolInfo',
    high_school_type: 'schoolInfo',
    hs_competitiveness: 'schoolInfo',
    
    // Academic
    gpa_weighted: 'academic',
    gpa_unweighted: 'academic',
    gpa_scale: 'academic',
    class_rank: 'academic',
    class_size: 'academic',
    
    // Test Scores
    sat_total: 'testScores',
    sat_math: 'testScores',
    sat_reading_writing: 'testScores',
    act_composite: 'testScores',
    national_merit_status: 'testScores',
    
    // Coursework
    ap_courses_taken: 'coursework',
    honors_courses: 'coursework',
    dual_enrollment_credits: 'coursework',
    curriculum_rigor: 'coursework',
    
    // Activities
    leadership_activities: 'activities',
    extracurriculars: 'activities',
    time_commitment: 'activities',
    academic_awards: 'activities',
    special_talents: 'activities',
    
    // Goals
    intended_major: 'goals',
    career_goals: 'goals',
    academic_interests: 'goals',
    college_preferences: 'goals',
    financial_aid_needed: 'goals',
    
    // Background
    first_generation_college: 'background',
    languages: 'background',
    geographic_region: 'background',
    special_circumstances: 'background'
  };

  // Get section display name
  const getSectionName = (sectionId) => {
    const sectionNames = {
      basicInfo: 'Basic Information',
      schoolInfo: 'School Information',
      academic: 'Academic Performance',
      testScores: 'Standardized Test Scores',
      coursework: 'Coursework & Rigor',
      activities: 'Activities & Leadership',
      goals: 'Goals & Aspirations',
      background: 'Personal Background'
    };
    return sectionNames[sectionId] || sectionId;
  };

  // Get field display name
  const getFieldDisplayName = (field) => {
    const fieldNames = {
      hs_competitiveness: 'School Competitiveness',
      curriculum_rigor: 'Curriculum Rigor',
      time_commitment: 'Time Commitment (Activities)',
      financial_aid_needed: 'Financial Context',
      gpa_weighted: 'Weighted GPA',
      gpa_unweighted: 'Unweighted GPA',
      gpa_scale: 'GPA Scale',
      class_rank: 'Class Rank',
      class_size: 'Class Size',
      sat_total: 'SAT Total',
      sat_math: 'SAT Math',
      sat_reading_writing: 'SAT Reading/Writing',
      act_composite: 'ACT Composite',
      national_merit_status: 'National Merit Status',
      ap_courses_taken: 'AP Courses Taken',
      honors_courses: 'Honors Courses',
      dual_enrollment_credits: 'Dual Enrollment Credits',
      leadership_activities: 'Leadership Roles',
      extracurriculars: 'Key Activities',
      academic_awards: 'Academic Awards',
      special_talents: 'Special Talents',
      intended_major: 'Intended Major',
      career_goals: 'Career Goals',
      academic_interests: 'Academic Interests',
      college_preferences: 'College Preferences',
      first_generation_college: 'First Generation College',
      languages: 'Languages Spoken',
      geographic_region: 'Geographic Context',
      special_circumstances: 'Special Circumstances',
      high_school_name: 'High School Name',
      high_school_city: 'High School City',
      high_school_state: 'High School State',
      high_school_type: 'School Type',
      grade_level: 'Grade Level',
      graduation_year: 'Graduation Year',
      state_of_residency: 'State of Residency'
    };
    return fieldNames[field] || field;
  };

  // Calculate AI confidence breakdown
  const getConfidenceBreakdown = () => {
    // Use the passed aiConfidence prop as the source of truth for consistency
    // The breakdown items show what factors affect confidence, but the final score
    // comes from the parent component to avoid calculation discrepancies
    const breakdown = [];
    
    // Missing critical fields
    const criticalMissing = completeness?.missingFields?.critical?.length || 0;
    if (criticalMissing > 0) {
      const deduction = criticalMissing * 2;
      breakdown.push({
        label: `Missing Critical Fields (${criticalMissing})`,
        value: -deduction,
        color: 'text-red-600'
      });
    }
    
    // Missing important fields
    const importantMissing = completeness?.missingFields?.important?.length || 0;
    if (importantMissing > 0) {
      const deduction = importantMissing * 1;
      breakdown.push({
        label: `Missing Important Fields (${importantMissing})`,
        value: -deduction,
        color: 'text-amber-600'
      });
    }
    
    // Missing optional fields
    const optionalMissing = completeness?.missingFields?.optional?.length || 0;
    if (optionalMissing > 0) {
      const deduction = Math.min(optionalMissing * 2, 5);
      breakdown.push({
        label: `Missing Optional Fields (${optionalMissing})`,
        value: -deduction,
        color: 'text-yellow-600'
      });
    }
    
    // Inconsistencies
    if (inconsistencies?.length > 0) {
      const majorInconsistencies = inconsistencies.filter(i => i.type === 'error').length;
      const minorInconsistencies = inconsistencies.filter(i => i.type === 'warning').length;
      const deduction = (majorInconsistencies * 5) + (minorInconsistencies * 2);
      breakdown.push({
        label: `Data Inconsistencies (${inconsistencies.length})`,
        value: -deduction,
        color: 'text-orange-600'
      });
    }
    
    // Use the passed aiConfidence prop as the final score (ensures consistency)
    // Default to 0 if not provided
    const finalScore = typeof aiConfidence === 'number' ? aiConfidence : 0;
    
    return { breakdown, finalScore };
  };

  const confidenceData = getConfidenceBreakdown();

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-full sm:max-w-3xl w-full max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-stone-200 flex justify-between items-center bg-slate-900 text-white">
          <h2 className="text-lg sm:text-xl font-bold">Profile Diagnostics</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
          >
            <X className="w-4 sm:w-5 h-4 sm:h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-stone-200 bg-stone-50">
          <button
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'missing'
                ? 'bg-white text-slate-900 border-b-2 border-slate-900'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            onClick={() => setActiveTab('missing')}
          >
            Missing Fields ({(completeness?.missingFields?.critical?.length || 0) + 
                            (completeness?.missingFields?.important?.length || 0) + 
                            (completeness?.missingFields?.optional?.length || 0)})
          </button>
          <button
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'inconsistencies'
                ? 'bg-white text-slate-900 border-b-2 border-slate-900'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            onClick={() => setActiveTab('inconsistencies')}
          >
            Inconsistencies ({inconsistencies?.length || 0})
          </button>
          <button
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'confidence'
                ? 'bg-white text-slate-900 border-b-2 border-slate-900'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            onClick={() => setActiveTab('confidence')}
          >
            AI Confidence ({aiConfidence}%)
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {activeTab === 'missing' && (
            <div className="space-y-6">
              {/* Critical Missing Fields */}
              {completeness?.missingFields?.critical?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                    Critical Missing Fields
                  </h3>
                  <div className="space-y-2">
                    {(completeness.missingFields.critical || []).map((item) => {
                      const fieldName = typeof item === 'object' ? item.field : item;
                      const sectionName = typeof item === 'object' ? item.section : fieldToSection[fieldName];
                      return (
                        <div
                          key={fieldName}
                          className="flex justify-between items-center p-2 sm:p-3 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors cursor-pointer"
                          onClick={() => onNavigateToField(fieldName, fieldToSection[fieldName])}
                        >
                          <div>
                            <div className="font-medium text-slate-900">
                              {getFieldDisplayName(fieldName)}
                            </div>
                            <div className="text-sm text-slate-600">
                              {getSectionName(fieldToSection[fieldName])}
                            </div>
                        </div>
                        <button className="flex items-center gap-1 px-2 sm:px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm">
                          Fix Now
                          <ChevronRight className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                        </button>
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}

              {/* Important Missing Fields */}
              {completeness?.missingFields?.important?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                    Important Missing Fields
                  </h3>
                  <div className="space-y-2">
                    {(completeness.missingFields.important || []).map((item) => {
                      const fieldName = typeof item === 'object' ? item.field : item;
                      const sectionName = typeof item === 'object' ? item.section : fieldToSection[fieldName];
                      return (
                        <div
                          key={fieldName}
                          className="flex justify-between items-center p-2 sm:p-3 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors cursor-pointer"
                          onClick={() => onNavigateToField(fieldName, fieldToSection[fieldName])}
                        >
                          <div>
                            <div className="font-medium text-slate-900">
                              {getFieldDisplayName(fieldName)}
                            </div>
                            <div className="text-sm text-slate-600">
                              {getSectionName(fieldToSection[fieldName])}
                            </div>
                        </div>
                        <button className="flex items-center gap-1 px-2 sm:px-3 py-1 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors text-sm">
                          Add Now
                          <ChevronRight className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                        </button>
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}

              {/* Optional Missing Fields */}
              {completeness?.missingFields?.optional?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-azure-700 mb-3 flex items-center gap-2">
                    <Info className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                    Optional Fields to Enhance Profile
                  </h3>
                  <div className="space-y-2">
                    {(completeness.missingFields.optional || []).map((item) => {
                      const fieldName = typeof item === 'object' ? item.field : item;
                      const sectionName = typeof item === 'object' ? item.section : fieldToSection[fieldName];
                      // Find impact forecast for this field
                      const forecast = impactForecasts?.find(f => f.field === fieldName);
                      return (
                        <div
                          key={fieldName}
                          className="flex justify-between items-center p-2 sm:p-3 bg-azure-50 border border-azure-200 rounded-lg hover:bg-azure-100 transition-colors cursor-pointer"
                          onClick={() => onNavigateToField(fieldName, fieldToSection[fieldName])}
                        >
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">
                              {getFieldDisplayName(fieldName)}
                            </div>
                            <div className="text-sm text-slate-600">
                              {getSectionName(fieldToSection[fieldName])}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {forecast && (
                              <span className="bg-teal-500 text-white px-2 py-1 rounded text-xs font-semibold">
                                +{forecast.impact}%
                              </span>
                            )}
                            <button className="flex items-center gap-1 px-2 sm:px-3 py-1 bg-azure-600 text-white rounded-md hover:bg-azure-700 transition-colors text-sm">
                              Add
                              <ChevronRight className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* All fields complete message */}
              {(!completeness?.missingFields?.critical?.length && 
                !completeness?.missingFields?.important?.length && 
                !completeness?.missingFields?.optional?.length) && (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    All Fields Complete!
                  </h3>
                  <p className="text-slate-600">
                    Your profile is fully complete. Great job!
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'inconsistencies' && (
            <div className="space-y-4">
              {inconsistencies?.length > 0 ? (
                inconsistencies.map((inconsistency, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                      inconsistency.type === 'error'
                        ? 'bg-red-50 border-red-200 hover:bg-red-100'
                        : 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                    }`}
                    onClick={() => onNavigateToField(inconsistency.field, fieldToSection[inconsistency.field])}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle 
                            className={`w-3.5 sm:w-4 h-3.5 sm:h-4 ${
                              inconsistency.type === 'error' ? 'text-red-600' : 'text-amber-600'
                            }`} 
                          />
                          <span className="font-semibold text-slate-900">
                            {getFieldDisplayName(inconsistency.field)}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            inconsistency.type === 'error' 
                              ? 'bg-red-200 text-red-700' 
                              : 'bg-amber-200 text-amber-700'
                          }`}>
                            {inconsistency.type === 'error' ? 'Major' : 'Minor'}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 mb-2">
                          {inconsistency.message}
                        </p>
                        <p className="text-xs text-stone-500">
                          Section: {getSectionName(fieldToSection[inconsistency.field])}
                        </p>
                      </div>
                      <button className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-md text-white text-sm transition-colors ${
                        inconsistency.type === 'error'
                          ? 'bg-red-600 hover:bg-red-700'
                          : 'bg-amber-600 hover:bg-amber-700'
                      }`}>
                        Fix Now
                        <ChevronRight className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    No Inconsistencies Found
                  </h3>
                  <p className="text-slate-600">
                    Your profile data is consistent and reliable.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'confidence' && (
            <div className="space-y-6">
              {/* Current Score */}
              <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900">
                    AI Confidence Score
                  </h3>
                  <span className={`text-3xl font-bold ${
                    aiConfidence >= 80 ? 'text-emerald-600' :
                    aiConfidence >= 60 ? 'text-amber-600' :
                    'text-red-600'
                  }`}>
                    {aiConfidence}%
                  </span>
                </div>
                <div className="w-full bg-stone-200 rounded-full h-3 mb-4">
                  <div
                    className={`h-3 rounded-full transition-all duration-500 ${
                      aiConfidence >= 80 ? 'bg-emerald-500' :
                      aiConfidence >= 60 ? 'bg-amber-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${aiConfidence}%` }}
                  />
                </div>
                <p className="text-sm text-slate-600">
                  {aiConfidence >= 80 
                    ? 'Excellent! AI has high confidence in your profile data.'
                    : aiConfidence >= 60
                    ? 'Good. Adding more information will improve AI accuracy.'
                    : 'Needs improvement. Complete missing fields to enhance AI insights.'}
                </p>
              </div>

              {/* Confidence Breakdown */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                  Confidence Breakdown
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 sm:p-3 bg-stone-50 rounded-lg">
                    <span className="text-sm text-slate-700">Base Score</span>
                    <span className="text-sm font-semibold text-slate-900">100%</span>
                  </div>
                  
                  {confidenceData.breakdown.map((item, index) => (
                    <div key={index} className="flex justify-between items-center p-2 sm:p-3 bg-stone-50 rounded-lg">
                      <span className={`text-sm ${item.color}`}>{item.label}</span>
                      <span className={`text-sm font-semibold ${item.color}`}>
                        {item.value}%
                      </span>
                    </div>
                  ))}
                  
                  <div className="flex justify-between items-center p-2 sm:p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <span className="text-sm font-semibold text-indigo-900">Final Score</span>
                    <span className="text-sm font-bold text-indigo-900">
                      {confidenceData.finalScore}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                  <TrendingUp className="w-3.5 sm:w-4 h-3.5 sm:h-4 inline mr-1" />
                  Top Actions to Improve Confidence
                </h3>
                <div className="space-y-2">
                  {completeness?.missingFields?.critical?.length > 0 && (
                    <div className="p-2 sm:p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700">
                        Complete {completeness.missingFields.critical.length} critical fields for +{completeness.missingFields.critical.length * 10}% confidence
                      </p>
                    </div>
                  )}
                  {inconsistencies?.filter(i => i.type === 'error').length > 0 && (
                    <div className="p-2 sm:p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-700">
                        Fix {inconsistencies.filter(i => i.type === 'error').length} major inconsistencies for +{inconsistencies.filter(i => i.type === 'error').length * 10}% confidence
                      </p>
                    </div>
                  )}
                  {completeness?.missingFields?.important?.length > 0 && (
                    <div className="p-2 sm:p-3 bg-azure-50 border border-azure-200 rounded-lg">
                      <p className="text-sm text-azure-700">
                        Add {Math.min(3, completeness.missingFields.important.length)} important fields for +{Math.min(3, completeness.missingFields.important.length) * 5}% confidence
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileDiagnosticsModal;